// P2b-3 §6.1 + round-1 polish Fix 5: URL search-param codec for the
// role grids' filter bar. Search + AM + Campaign + Group + Status +
// Builder + "Assigned to me" filters survive bookmarks; sort does not
// (§6.2 ruling, transient state).
//
// Wire form:
//   ?q=<text>
//   &am=<uid1,uid2>
//   &campaign=<id1,id2>
//   &group=<id1,id2|__no_group__>
//   &status=<stage>
//   &builder=<uid1,uid2|__unassigned__>
//   &me=1
//   &assets=<hasLocal,noLocal,hasOrganic,noOrganic>          (Meta grid only)
//   &build=<mainNotBuilt,localNotBuilt,organicNotBuilt>      (Meta grid only)
//
// Empty / default fields are omitted. Status uses hyphenated stage
// names so URLs stay readable.

export const URL_PARAM = {
  search: 'q',
  am: 'am',
  campaign: 'campaign',
  group: 'group',
  status: 'status',
  builder: 'builder',
  me: 'me',
  assets: 'assets',
  build: 'build',
} as const;

import {
  sanitizeAssetPresence,
  sanitizeBuildStatus,
  type AssetPresenceFilter,
  type BuildStatusFilter,
} from './assetBuildFilters.js';

// Sentinel values reused by the UI dropdowns + this codec. Stable wire
// strings so URLs survive across refactors.
export const NO_GROUP = '__no_group__';
export const UNASSIGNED_BUILDER = '__unassigned__';

// Status stages — the role-grid version. Two parallel sets keep Meta /
// Google wording explicit; the URL stores the hyphenated wire form.
// Mapping these to predicates lives in applyGridFilters.ts.
export type MetaStatusFilter =
  | 'all'
  | 'not-built'
  | 'built-not-live'
  | 'live-not-postcheck'
  | 'complete';

export type GoogleStatusFilter =
  | 'all'
  | 'not-built'
  | 'built-not-scheduled'
  | 'scheduled-not-postcheck'
  | 'complete';

export type StatusFilter = MetaStatusFilter | GoogleStatusFilter;

// All known status wire values. Anything else parses to 'all' to keep
// stale URLs from blanking the grid.
const META_STATUSES: ReadonlySet<string> = new Set<MetaStatusFilter>([
  'all',
  'not-built',
  'built-not-live',
  'live-not-postcheck',
  'complete',
]);
const GOOGLE_STATUSES: ReadonlySet<string> = new Set<GoogleStatusFilter>([
  'all',
  'not-built',
  'built-not-scheduled',
  'scheduled-not-postcheck',
  'complete',
]);

export type GridFilters = {
  search: string;
  am: string[];
  campaignIds: string[];
  groups: string[];
  // The platform owns its own status enum; we keep the field as
  // string-typed here and let applyGridFilters narrow + ignore unknown
  // values per platform. (Avoids leaking a `platform` argument through
  // the URL codec.)
  status: StatusFilter;
  builders: string[];
  assignedToMe: boolean;
  // Organic Assets filters. Meta grid only — the Google grid never renders
  // the controls and applyGridFilters ignores them for platform='google'.
  assetPresence: AssetPresenceFilter[];
  buildStatus: BuildStatusFilter[];
};

export const DEFAULT_GRID_FILTERS: GridFilters = {
  search: '',
  am: [],
  campaignIds: [],
  groups: [],
  status: 'all',
  builders: [],
  assignedToMe: false,
  assetPresence: [],
  buildStatus: [],
};

// Read the URL params into the typed filter state. Unknown / missing
// values fall back to defaults so a malformed URL doesn't blank the
// grid.
//
// `platform` is optional: when omitted, Status decodes from EITHER
// status set (so callers that only care about non-Status filters
// don't need to care). When supplied, the codec narrows so a stale
// URL with a Google-only status on a Meta grid (or vice versa) reads
// as 'all' rather than rendering an unintelligible filter.
export function readGridFiltersFromParams(
  params: URLSearchParams,
  platform?: 'meta' | 'google',
): GridFilters {
  const search = params.get(URL_PARAM.search) ?? '';
  const am = parseList(params.get(URL_PARAM.am));
  const campaignIds = parseList(params.get(URL_PARAM.campaign));
  const groups = parseList(params.get(URL_PARAM.group));
  const builders = parseList(params.get(URL_PARAM.builder));
  const me = params.get(URL_PARAM.me) === '1';
  const assetPresence = sanitizeAssetPresence(parseList(params.get(URL_PARAM.assets)));
  const buildStatus = sanitizeBuildStatus(parseList(params.get(URL_PARAM.build)));

  const statusRaw = params.get(URL_PARAM.status);
  const validSet =
    platform === 'meta'
      ? META_STATUSES
      : platform === 'google'
        ? GOOGLE_STATUSES
        : new Set<string>([...META_STATUSES, ...GOOGLE_STATUSES]);
  const status: StatusFilter =
    statusRaw && validSet.has(statusRaw) ? (statusRaw as StatusFilter) : 'all';

  return {
    search,
    am,
    campaignIds,
    groups,
    status,
    builders,
    assignedToMe: me,
    assetPresence,
    buildStatus,
  };
}

// Inverse codec — mutates the passed URLSearchParams in place. Caller
// decides between push / replace history. Default values omitted.
export function writeGridFiltersToParams(
  params: URLSearchParams,
  state: GridFilters,
): void {
  for (const key of Object.values(URL_PARAM)) params.delete(key);
  const trimmed = state.search.trim();
  if (trimmed.length > 0) params.set(URL_PARAM.search, trimmed);
  if (state.am.length > 0) {
    params.set(URL_PARAM.am, [...state.am].sort().join(','));
  }
  if (state.campaignIds.length > 0) {
    params.set(URL_PARAM.campaign, [...state.campaignIds].sort().join(','));
  }
  if (state.groups.length > 0) {
    params.set(URL_PARAM.group, [...state.groups].sort().join(','));
  }
  if (state.status !== 'all') {
    params.set(URL_PARAM.status, state.status);
  }
  if (state.builders.length > 0) {
    params.set(URL_PARAM.builder, [...state.builders].sort().join(','));
  }
  if (state.assignedToMe) {
    params.set(URL_PARAM.me, '1');
  }
  if (state.assetPresence.length > 0) {
    params.set(URL_PARAM.assets, [...state.assetPresence].sort().join(','));
  }
  if (state.buildStatus.length > 0) {
    params.set(URL_PARAM.build, [...state.buildStatus].sort().join(','));
  }
}

export function gridFiltersAreDefault(state: GridFilters): boolean {
  return (
    state.search.trim().length === 0 &&
    state.am.length === 0 &&
    state.campaignIds.length === 0 &&
    state.groups.length === 0 &&
    state.status === 'all' &&
    state.builders.length === 0 &&
    !state.assignedToMe &&
    state.assetPresence.length === 0 &&
    state.buildStatus.length === 0
  );
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
